import type { LeadFormDTO } from 'api-models'
import {
	defaults,
	LeadFormsApi,
	Stats,
	Submission,
	SubmissionQuery
} from './api'

/** Demo data never uses a customer's API, credentials, or submission records. */
export const createDemoLeadFormsApi = (): LeadFormsApi => {
	const forms = ['Contact sales', 'Newsletter signup'].map((name, index) => ({
		...defaults(),
		id: `demo-form-${index}`,
		account_id: 'account-demo',
		asset_id: 'website-demo',
		user_id: null,
		form_name: name,
		slug: name.toLowerCase().replace(/ /g, '-'),
		form_url: `https://demo.example/${index ? 'newsletter' : 'contact'}`,
		email_field_detected: true,
		last_checked_at: new Date().toISOString(),
		status: 'created',
		total_form_submissions: 35,
		total_flagged_submissions: 7,
		total_blocked_bots: 4,
		totals_updated_at: null,
		created_at: new Date().toISOString(),
		updated_at: new Date().toISOString(),
		deleted_at: null
	})) as LeadFormDTO[]
	const rows: Submission[] = Array.from({ length: 28 }, (_, index) => ({
		id: `demo-submission-${index}`,
		form_id: forms[index % 2].id,
		form_name: forms[index % 2].form_name,
		form_url: forms[index % 2].form_url,
		created_at: new Date(
			Date.now() - index * 12 * 60 * 60 * 1000
		).toISOString(),
		email: `visitor${index + 1}@example.com`,
		email_outcome: index % 5 === 0 ? 'invalid' : 'valid',
		bot_outcome: index % 4 === 0 ? 'solved' : 'normal',
		flagged: index % 5 === 0,
		source: index % 3 === 0 ? 'Google Ads' : 'Other',
		city: 'London',
		country: 'GB',
		location: 'London, GB',
		client_ip: `192.0.2.${index + 1}`
	}))
	const now = Date.now()
	const rangeStart = (days: number) => {
		const date = new Date(now)
		return (
			Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) -
			(days - 1) * 86400000
		)
	}
	const matches = (q: SubmissionQuery) =>
		rows
			.filter(
				(row) =>
					Date.parse(row.created_at) >= rangeStart(q.days) &&
					(!q.form_id || row.form_id === q.form_id) &&
					(!q.email_outcome || row.email_outcome === q.email_outcome) &&
					(!q.bot_outcome || row.bot_outcome === q.bot_outcome) &&
					(!q.source || row.source === q.source) &&
					(!q.location ||
						row.location.toLowerCase().includes(q.location.toLowerCase())) &&
					(!q.query ||
						`${row.email} ${row.form_name} ${row.client_ip}`
							.toLowerCase()
							.includes(q.query.toLowerCase()))
			)
			.sort((a, b) => {
				const key = (
					!q.sort_by || q.sort_by === 'date' ? 'created_at' : q.sort_by
				) as keyof Submission
				return (
					String(a[key] || '').localeCompare(String(b[key] || '')) *
					(q.sort === 'asc' ? 1 : -1)
				)
			})
	const unavailable = async (): Promise<any> => {
		throw new Error('Demo forms cannot be changed')
	}
	return {
		accountId: '',
		assetId: '',
		list: async () => forms,
		settings: async () => ({
			site_key: 'website-demo',
			secret: null,
			can_write: false,
			siteverify_url: '/v1/siteverify'
		}),
		stats: async (days, form_id) => {
			const from = rangeStart(days),
				previousFrom = from - days * 86400000
			const selected = rows.filter((row) => !form_id || row.form_id === form_id)
			const current = selected.filter(
				(row) => Date.parse(row.created_at) >= from
			)
			const previous = selected.filter(
				(row) =>
					Date.parse(row.created_at) >= previousFrom &&
					Date.parse(row.created_at) < now - days * 86400000
			)
			const metrics = (items: Submission[]) => ({
				submissions: items.length,
				flagged: items.filter((row) => row.flagged).length,
				blocked: 0
			})
			const series = (key: 'email_outcome' | 'bot_outcome') =>
				Array.from({ length: days }, (_, index) => {
					const day = new Date(from + index * 86400000)
						.toISOString()
						.slice(0, 10)
					return {
						day,
						counts: current
							.filter((row) => row.created_at.startsWith(day))
							.reduce<Record<string, number>>(
								(counts, row) => ({
									...counts,
									[row[key]]: (counts[row[key]] || 0) + 1
								}),
								{}
							)
					}
				})
			const distribution = (
				key: 'email_outcome' | 'bot_outcome',
				keys: string[]
			) =>
				keys.map((outcome) => {
					const count = current.filter((row) => row[key] === outcome).length,
						before = previous.filter((row) => row[key] === outcome).length,
						percentage = current.length ? (count / current.length) * 100 : 0,
						previous_percentage = previous.length
							? (before / previous.length) * 100
							: 0
					return {
						key: outcome,
						count,
						percentage,
						previous_percentage,
						delta: percentage - previous_percentage
					}
				})
			return {
				forms: forms
					.filter((f) => !form_id || f.id === form_id)
					.map((f) => ({
						...metrics(current.filter((r) => r.form_id === f.id)),
						previous: metrics(previous.filter((r) => r.form_id === f.id)),
						form_id: f.id,
						last_submission_at: rows.find((r) => r.form_id === f.id)!.created_at
					})),
				metrics: {
					...metrics(current),
					previous: metrics(previous),
					delta: { submissions: 0, flagged: 0, blocked: 0 }
				},
				email_series: series('email_outcome'),
				bot_series: series('bot_outcome'),
				email_outcomes: distribution('email_outcome', [
					'valid',
					'alias',
					'catchAll',
					'invalid',
					'spamTraps',
					'doNotMail',
					'unknown'
				]),
				bot_outcomes: distribution('bot_outcome', [
					'normal',
					'solved',
					'captcha_off',
					'not_solved',
					'blocked'
				])
			} as Stats
		},
		submissions: async (q) => {
			const match = matches(q)
			return {
				rows: match.slice((q.page - 1) * q.limit, q.page * q.limit),
				total: match.length,
				page: q.page,
				limit: q.limit
			}
		},
		export: async (q) => {
			const columns = [
				'created_at',
				'form_name',
				'email',
				'source',
				'location',
				'client_ip',
				'email_outcome'
			]
			const lines = matches(q).map((row) =>
				columns
					.map(
						(key) =>
							'"' +
							String(row[key as keyof Submission] || '').replace(/"/g, '""') +
							'"'
					)
					.join(',')
			)
			return new Blob([columns.join(',') + '\r\n' + lines.join('\r\n')], {
				type: 'text/csv'
			})
		},
		check: unavailable,
		verify: unavailable,
		create: unavailable,
		update: unavailable,
		remove: unavailable
	}
}
